'use client';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import type { LiveCourse } from '@/types/course';
import dayjs from 'dayjs';
import { useUser } from '@clerk/nextjs';
import type { BookingResponse } from '@/types/booking';
import { BookingSource, BookingTypes } from '@/types/booking';
import BookingButtons from './BookingButtons';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import { formatDate } from '@/utils/helpers';
import { T } from '@tolgee/react';
import { useBookingService } from '@/hooks/useBookingService';
import { useHandleBooking } from '@/hooks/useHandleBooking';

interface LiveCourseMainCardProps {
  course: LiveCourse;
  type?: 'dark' | 'light';
  onOpenModal: (course: LiveCourse, success?: boolean) => void;
}

const LiveCourseMainCard: React.FC<LiveCourseMainCardProps> = ({
  course,
  type = 'dark',
  onOpenModal,
}) => {
  const isDark = type === 'dark';
  const { user } = useUser();
  const [isBooked, setIsBooked] = useState(false);
  const [booking, setBooking] = useState<BookingResponse | null>(null);
  const locale = useSelectedLanguage();
  const { findBooking } = useBookingService();
  const { handleBooking, isBookingSuccessful, bookingId } = useHandleBooking();

  useEffect(() => {
    if (!user?.id) return;

    const checkBooking = async () => {
      try {
        const booking: BookingResponse | null = await findBooking(
          user?.id || '',
          course.id.toString(),
        );
        setIsBooked(!!booking);
        setBooking(booking);
      } catch (_error) {
        console.error('Error checking booking:', _error);
      }
    };

    void checkBooking();
  }, [course.id, user?.id]);

  const handleBookingClick = async () => {
    await handleBooking({
      externalId: course.expertExternalId,
      userId: user?.id,
      type: BookingTypes.COURSE,
      time: dayjs(course.date).toDate(),
      courseId: course.id,
      locale,
      source: BookingSource.USER,
    });
    if (isBookingSuccessful) {
      onOpenModal(course, true);
    }
  };

  return (
    <div className="block md:flex bg-white rounded-2xl overflow-hidden h-auto md:h-[585px]">
      <div className="w-full h-80 md:h-auto md:w-1/2 relative flex md:hidden">
        <Image src={course?.imageSrc || ''} alt={course.title} fill objectFit="cover" />
      </div>
      <div
        className={`w-full md:w-1/2 p-12 relative ${
          isDark ? 'bg-primary text-white' : 'bg-gray text-black'
        }`}
      >
        <div className="mb-4">
          {isDark && course.closestEvent && (
            <span className="inline-block py-2.5 px-4 mb-4 rounded-full text-sm text-primary bg-white/50 backdrop-blur-[9.4px]">
              <T>live_class.next_class.tag.label</T>
            </span>
          )}
          <p className="text-base mb-4">
            {formatDate(course.date, locale)}, {course.time_range}
          </p>
          <h3 className="text-4xl mb-4">{course.title}</h3>
          <p
            className={`overflow-hidden font-poppins text-ellipsis line-clamp-3 ${
              isDark ? 'text-white/70' : 'text-black/70'
            }`}
          >
            {course.description}
          </p>
        </div>
        <div className="flex items-center mt-4">
          <div className="w-10 h-10 relative">
            <Image
              src={course.expertImage}
              alt={course.expertName}
              width={40}
              height={40}
              className="rounded-full object-cover w-full h-full"
            />
          </div>
          <p className="ml-2 text-base">{course.expertName}</p>
        </div>
        <BookingButtons
          isBooked={isBooked}
          bookingSuccessful={isBookingSuccessful}
          bookingId={booking?.id || bookingId}
          onOpenModal={() => onOpenModal(course)}
          handleBookingClick={() => {
            void handleBookingClick();
          }}
          courseId={course.id.toString()}
          isDark={isDark}
        />
      </div>
      <div className="h-auto w-1/2 relative hidden md:flex">
        <Image src={course.imageSrc || ''} alt={course.title} layout="fill" objectFit="cover" />
      </div>
    </div>
  );
};

export default LiveCourseMainCard;
